import { type ReactNode } from 'react';
import {
	type Key,
	Tab as RACTab,
	TabList as RACTabList,
	TabPanel as RACTabPanel,
	Tabs as RACTabs,
	type TabListProps,
	type TabPanelProps,
	type TabProps,
	type TabsProps as RACTabsProps,
	composeRenderProps
} from 'react-aria-components';
import { cn } from '../utils';
import { tv } from 'tailwind-variants';
import { baseTabStyles } from './Tabs';
import { PageInset } from '~/shared/PageInset';

export const insetTabStyles = tv({
	extend: baseTabStyles,
	base: 'gap-1.5 py-2.5 text-xs',
	variants: {
		isSelected: {
			true: 'border-primary-500 text-primary-600',
			false: 'border-transparent text-gray-500 hover:text-gray-700'
		},
		isDisabled: {
			true: 'cursor-not-allowed text-gray-300 opacity-50'
		}
	}
});

function Tab(props: TabProps) {
	return (
		<RACTab
			{...props}
			className={composeRenderProps(props.className, (className, renderProps) => insetTabStyles({ ...renderProps, className }))}
		/>
	);
}

function TabList<T extends object>(props: TabListProps<T>) {
	return (
		<RACTabList
			{...props}
			className={composeRenderProps(props.className, (className, renderProps) =>
				cn(
					'flex border-b border-gray-200',
					renderProps.orientation === 'vertical' ? 'flex-col items-start border-b-0 border-r' : 'flex-row',
					className
				)
			)}
		/>
	);
}

function TabPanel(props: Omit<TabPanelProps, 'children'> & { children: ReactNode }) {
	return (
		<RACTabPanel
			{...props}
			className={composeRenderProps(props.className, className => cn('relative flex-1 text-sm text-gray-900 bg-gray-100', className))}
		>
			<div className="absolute inset-0 overflow-y-auto">{props.children}</div>
		</RACTabPanel>
	);
}

function TabPanelWithNav(props: Omit<TabPanelProps, 'children'> & { navigation: ReactNode; children: ReactNode }) {
	return (
		<RACTabPanel
			{...props}
			className={composeRenderProps(props.className, className => cn('relative flex-1 text-sm text-gray-900 bg-gray-100', className))}
		>
			<PageInset.Nav>{props.navigation}</PageInset.Nav>
			<PageInset.Body>{props.children}</PageInset.Body>
		</RACTabPanel>
	);
}

export interface InsetTabsProps extends Omit<RACTabsProps, 'onSelectionChange'> {
	onSelectionChange: (newTab: string) => void;
	tabs: ReactNode[] | ReactNode;
}

export function InsetTabs(props: InsetTabsProps) {
	return (
		<RACTabs
			{...props}
			onSelectionChange={(key: Key) => props.onSelectionChange(key.toString())}
			className={cn('flex min-h-0 flex-1 flex-col gap-0', props.className)}
		>
			<TabList aria-label="Visit Sections">{props.tabs}</TabList>
			<PageInset>{props.children as ReactNode}</PageInset>
		</RACTabs>
	);
}

InsetTabs.Tab = Tab;
InsetTabs.TabPanel = TabPanel;
InsetTabs.TabPanelWithNav = TabPanelWithNav;
